import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { Button } from '../components/ui/button';
import JsonLd from '../components/JsonLd';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders a single semantic content block
const ContentBlock = ({ block }) => {
  switch (block.type) {
    case 'heading':
      return (
        <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-14 mb-5 tracking-tight scroll-mt-24">
          {block.text}
        </h2>
      );
    case 'quote':
      return (
        <blockquote className="my-10 border-l-2 border-[#C8102E] pl-6 py-1">
          <p className="text-xl md:text-2xl font-medium text-[#1A1A1A] leading-snug italic">
            {block.text}
          </p>
        </blockquote>
      );
    case 'list':
      return (
        <ul className="my-6 space-y-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start text-lg text-gray-700 leading-relaxed">
              <span className="font-mono text-[#C8102E] mr-3 mt-1 text-sm">▸</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'paragraph':
    default:
      return (
        <p className="text-lg text-gray-700 leading-[1.8] mb-6">{block.text}</p>
      );
  }
};

const BlogArticle = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | notfound

  useEffect(() => {
    setStatus('loading');
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => {
        setPost(res.data);
        setStatus('ready');
      })
      .catch(() => setStatus('notfound'));
  }, [slug]);

  // Fetch related posts — prefer same category, fill with most recent
  useEffect(() => {
    if (!post) return;
    axios
      .get(`${API}/blog`)
      .then((res) => {
        const all = (res.data || []).filter((p) => p.slug !== post.slug);
        const sameCat = all.filter((p) => p.category === post.category);
        const rest = all.filter((p) => p.category !== post.category);
        setRelated([...sameCat, ...rest].slice(0, 3));
      })
      .catch(() => setRelated([]));
  }, [post]);

  useSEO({
    title: post?.meta_title,
    description: post?.meta_description,
    image: post?.cover_image,
    type: 'article',
  });

  if (status === 'loading') {
    return (
      <div className="pt-40 pb-40 text-center bg-white min-h-screen">
        <p className="font-mono text-gray-400" data-testid="article-loading">Loading article…</p>
      </div>
    );
  }

  if (status === 'notfound') {
    return (
      <div className="pt-40 pb-40 text-center bg-white min-h-screen" data-testid="article-notfound">
        <p className="font-mono text-[#C8102E] mb-4">404 — article not found</p>
        <Link to="/blog">
          <Button className="bg-[#1A1A1A] hover:bg-[#C8102E] text-white rounded-full px-6">
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <article className="pt-32 bg-white" data-testid="blog-article">
      <JsonLd
        id="article"
        data={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.meta_description,
          image: post.cover_image,
          datePublished: post.published_at,
          dateModified: post.published_at,
          author: { '@type': 'Organization', name: post.author },
          publisher: {
            '@type': 'Organization',
            name: 'Hei Web Creations Pte Ltd',
            logo: {
              '@type': 'ImageObject',
              url: `${window.location.origin}/logo.png`,
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${window.location.origin}/blog/${post.slug}`,
          },
          keywords: (post.tags || []).join(', '),
        }}
      />
      {/* Header */}
      <header className="py-16 border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center font-mono text-sm text-gray-500 hover:text-[#C8102E] transition-colors mb-8"
              data-testid="back-to-blog-link"
            >
              <ArrowLeft size={16} className="mr-2" />
              cd ../insights
            </Link>

            <div className="flex flex-wrap items-center gap-3 mb-6 font-mono text-xs">
              <span className="text-[#C8102E] uppercase tracking-wider">{post.category}</span>
              <span className="text-gray-300">/</span>
              <span className="text-gray-400">{post.read_time}</span>
              <span className="text-gray-300">/</span>
              <span className="text-gray-400">{post.published_at}</span>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-[1.1] tracking-tight mb-6"
            >
              {post.title}
            </motion.h1>

            <p className="text-xl text-gray-500 leading-relaxed">{post.excerpt}</p>

            <div className="flex items-center gap-3 mt-8 pt-8 border-t border-gray-100">
              <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                <span className="font-mono text-white text-xs">H</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">{post.author}</p>
                <p className="text-xs text-gray-400 font-mono">Singapore</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Cover Image */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl overflow-hidden aspect-[16/9] bg-[#FAFAFA]">
            <img
              src={post.cover_image}
              alt={`Cover image for '${post.title}'`}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container mx-auto px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          {post.content.map((block, index) => (
            <ContentBlock key={index} block={block} />
          ))}

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-14 pt-8 border-t border-gray-100">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="font-mono text-xs text-gray-500 bg-[#FAFAFA] border border-gray-100 rounded-full px-3 py-1.5"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-20 bg-[#FAFAFA] border-y border-gray-100" data-testid="related-articles">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <div>
                  <span className="font-mono text-sm text-[#C8102E] tracking-tight">
                    ~/continue-reading
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-2 tracking-tight">
                    Related articles
                  </h2>
                </div>
                <Link
                  to="/blog"
                  className="font-mono text-sm text-[#1A1A1A] hover:text-[#C8102E] transition-colors inline-flex items-center gap-1"
                  data-testid="related-view-all"
                >
                  View all posts <ArrowRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {related.map((r, i) => (
                  <motion.article
                    key={r.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    data-testid={`related-article-${r.slug}`}
                  >
                    <Link to={`/blog/${r.slug}`} className="group block">
                      <div className="relative overflow-hidden rounded-2xl aspect-[16/10] mb-5 bg-white">
                        <img
                          src={r.cover_image}
                          alt={`Cover image for '${r.title}' — ${r.category} article by Hei Web Creations`}
                          loading="lazy"
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
                        />
                      </div>
                      <div className="flex items-center gap-3 mb-2 font-mono text-xs">
                        <span className="text-[#C8102E] uppercase tracking-wider">{r.category}</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-gray-400">{r.read_time}</span>
                      </div>
                      <h3 className="text-lg font-bold text-[#1A1A1A] leading-snug group-hover:text-[#C8102E] transition-colors">
                        {r.title}
                      </h3>
                      <p className="text-gray-500 mt-2 text-sm leading-relaxed line-clamp-2">
                        {r.excerpt}
                      </p>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA — Ready to launch? */}
      <section className="py-20 bg-white" data-testid="article-cta">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-[#1A1A1A] rounded-[2.5rem] px-8 py-16 md:px-16 relative overflow-hidden">
            {/* Ambient red glow */}
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#C8102E]/25 blur-[120px] pointer-events-none" />

            <div className="relative z-10 text-center">
              <span className="inline-block font-mono text-xs uppercase tracking-[0.3em] text-[#C8102E] mb-4">
                ~/next-step
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-5 tracking-tight leading-[1.05]">
                Ready to launch?
              </h2>
              <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                We turn insights like these into shipped websites — combining human design with AI workflows to launch faster and convert better.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/contact">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      className="bg-[#C8102E] hover:bg-white hover:text-[#1A1A1A] text-white px-10 py-6 text-base rounded-full transition-all duration-300"
                      data-testid="article-cta-primary"
                    >
                      Start Your Project
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </motion.div>
                </Link>
                <Link to="/services">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="ghost"
                      className="text-white hover:bg-white/10 px-8 py-6 text-base rounded-full transition-all duration-300"
                      data-testid="article-cta-secondary"
                    >
                      Explore Services →
                    </Button>
                  </motion.div>
                </Link>
              </div>

              {/* Credibility strip */}
              <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-mono text-xs text-gray-500">
                <span><span className="text-white font-semibold">150+</span> projects shipped</span>
                <span className="hidden sm:inline text-gray-700">·</span>
                <span><span className="text-white font-semibold">2-4</span> week average delivery</span>
                <span className="hidden sm:inline text-gray-700">·</span>
                <span><span className="text-white font-semibold">Singapore</span> based team</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};

export default BlogArticle;
